import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import { 
  TimeSeriesChart, 
  ClaimsComparisonChart, 
  StatusDistributionChart, 
  TopLendersChart 
} from './charts';
import { ProductivityStats } from './ProductivityStats';
import './Dashboard.css';
import {
  BuildingIcon,
  CalendarIcon,
  ChartBarIcon,
  EnvelopeIcon,
  FolderIcon,
  IconProps,
  SearchIcon,
  SparklesIcon,
  UsersIcon
} from './icons/HeroIcons';

interface CacheMetadata {
  cache_age_seconds?: number;
  last_updated?: string;
  error?: any;
}

interface IdVerificationStats {
  requests: {
    sent: number;
    pending: number;
    verified: number;
    rejected: number;
    notRequested: number;
    totalClients: number;
  };
  approvals: {
    totalReceived: number;
    pendingReview: number;
    manuallyApproved: number;
    aiApproved: number;
    totalApproved: number;
    rejected: number;
  };
  aiPerformance: {
    processed: number;
    approved: number;
    approvalRate: number;
  };
}

interface AnalyticsData {
  totalClaims: number;
  nonLegacyClaims: number;
  legacyClaims?: number;
  claimsByStatus: { [key: string]: number };
  locsSent: number;
  uniqueLenders: number;
  uniqueClients: number;
  recentClaims: number;
  totalAudits: number;
  auditsByUser: { [key: string]: number };
  topLenders: Array<{ lender: string; count: number }>;
  includesLegacyClaims?: boolean;
  idVerification?: IdVerificationStats;
  _cache_metadata?: CacheMetadata;
}

interface TimeSeriesData {
  timeSeries: Array<{
    period: string;
    claims_received: number;
    locs_sent: number;
    responses_received: number;
    cancelled_claims: number;
  }>;
  granularity: string;
  dateRange: {
    start: string | null;
    end: string | null;
  };
  totalPeriods: number;
  _cache_metadata?: CacheMetadata;
}

interface AuditAnalytics {
  totalAudits: number;
  auditsByUser: { [key: string]: number };
  auditsByUserDetailed: { 
    [key: string]: { 
      total: number; 
      today: number; 
      thisWeek: number; 
      thisMonth: number; 
    } 
  };
  auditsToday: number;
  auditsThisWeek: number;
  auditsThisMonth: number;
  auditsByDay: Array<{ date: string; count: number }>;
  recentAudits: Array<any>;
  _cache_metadata?: CacheMetadata;
}

interface AnalyticsSummary {
  claimTrends: {
    today: number;
    yesterday: number;
    last7Days: number;
    last30Days: number;
    dailyChange: number;
    weeklyAverage: number;
    monthlyAverage: number;
  };
  locTrends: {
    today: number;
    last7Days: number;
    weeklyAverage: number;
  };
  auditTrends: {
    today: number;
    last7Days: number;
    weeklyAverage: number;
  };
  cancellationTrends?: {
    today: number;
    yesterday: number;
    last7Days: number;
    last30Days: number;
    dailyChange: number;
    weeklyAverage: number;
    monthlyAverage: number;
  };
  _cache_metadata?: CacheMetadata;
}

interface CafBacklogProcessorResults {
  totalProcessed: number;
  totalSuccess: number;
  totalErrors: number;
  tasksArchived: number;
  tasksAssigned: number;
  tasksCompleted: number;
  actionCounts?: {
    AUTO_ARCHIVE?: number;
    AUTO_ASSIGN_COMPLETE?: number;
    AUTO_ASSIGN_ONLY?: number;
    MANUAL_REVIEW?: number;
  };
}

interface CafBacklogStatus {
  enabled: boolean;
  batchSize: number;
  pendingTasks: number;
  nextRun?: string | null;
  jobActive: boolean;
  lastRun?: {
    time: string | null;
    results?: CafBacklogProcessorResults | null;
  };
}

type IconRenderer = React.ComponentType<IconProps>;

interface MetricCardConfig {
  key: string;
  label: string;
  value: string;
  subtitle?: string;
  icon: IconRenderer;
  accent: string;
}

const Dashboard: React.FC = () => {
  const [analytics, setAnalytics] = useState<AnalyticsData | null>(null);
  const [timeSeries, setTimeSeries] = useState<TimeSeriesData | null>(null);
  const [auditAnalytics, setAuditAnalytics] = useState<AuditAnalytics | null>(null);
  const [summary, setSummary] = useState<AnalyticsSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [dateRange, setDateRange] = useState({ start: '', end: '' });
  const [granularity, setGranularity] = useState<'daily' | 'weekly' | 'monthly'>('daily');
  const [cacheAge, setCacheAge] = useState<number | null>(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [showEnhancedView, setShowEnhancedView] = useState(false);
  const [cafBacklogStats, setCafBacklogStats] = useState<CafBacklogStatus | null>(null);
  
  const { apiRequest } = useAuth();

  const fetchAnalytics = async () => {
    try {
      setLoading(true);
      setError('');
      
      // Build URLs with query parameters
      const buildUrlWithParams = (baseUrl: string, params: Record<string, string>) => {
        const url = new URL(baseUrl, window.location.origin);
        Object.entries(params).forEach(([key, value]) => {
          if (value) {
            url.searchParams.append(key, value);
          }
        });
        return url.pathname + url.search;
      };

      const timeSeriesUrl = buildUrlWithParams('/ui/admin/analytics/time-series', {
        granularity,
        ...(dateRange.start && { start_date: dateRange.start }),
        ...(dateRange.end && { end_date: dateRange.end })
      });

      const auditUrl = buildUrlWithParams('/ui/admin/analytics/audits', {
        ...(dateRange.start && { start_date: dateRange.start }),
        ...(dateRange.end && { end_date: dateRange.end })
      });
      
      // Fetch all analytics data in parallel - use cached endpoints for speed
      const [basicAnalytics, timeSeriesData, auditData, summaryData, cafStatus] = await Promise.all([
        apiRequest<AnalyticsData>({
          url: '/ui/admin/analytics/dashboard/cached',
          method: 'GET'
        }),
        apiRequest<TimeSeriesData>({
          url: timeSeriesUrl.replace('/time-series', '/time-series/cached'),
          method: 'GET'
        }),
        apiRequest<AuditAnalytics>({
          url: auditUrl.replace('/audits', '/audits/cached'),
          method: 'GET'
        }),
        apiRequest<AnalyticsSummary>({
          url: '/ui/admin/analytics/summary/cached',
          method: 'GET'
        }),
        apiRequest<CafBacklogStatus>({
          url: '/ui/admin/processors/caf-backlog/status',
          method: 'GET'
        }).catch(() => null)
      ]);
      
      setAnalytics(basicAnalytics);
      setTimeSeries(timeSeriesData);
      setAuditAnalytics(auditData);
      setSummary(summaryData);
      
      if (cafStatus) {
        setCafBacklogStats(cafStatus);
      }
      
      // Extract cache age from any response that has it
      const cacheMetadata = basicAnalytics._cache_metadata || 
                           timeSeriesData._cache_metadata || 
                           auditData._cache_metadata || 
                           summaryData._cache_metadata;
      if (cacheMetadata?.cache_age_seconds !== undefined) {
        setCacheAge(cacheMetadata.cache_age_seconds);
      }
    } catch (err: any) {
      console.error('Error fetching analytics:', err);
      setError(err.message || 'Failed to load analytics data');
    } finally {
      setLoading(false);
    }
  };

  const applyDateFilter = () => {
    fetchAnalytics();
  };

  const clearDateFilter = () => {
    setDateRange({ start: '', end: '' });
    setTimeout(fetchAnalytics, 100);
  };

  const refreshCache = async () => {
    try {
      setIsRefreshing(true);
      setError('');
      
      await apiRequest({
        url: '/ui/admin/dashboard-stats-cache/refresh',
        method: 'POST'
      });
      
      // Wait a moment for the cache to update
      setTimeout(() => {
        fetchAnalytics();
      }, 1000);
    } catch (err: any) {
      console.error('Error refreshing cache:', err);
      setError(err.message || 'Failed to refresh cache');
    } finally {
      setIsRefreshing(false);
    }
  };

  const formatCacheAge = (seconds: number | null): string => {
    if (seconds === null) return '';
    
    if (seconds < 60) {
      return `${seconds} seconds ago`;
    } else if (seconds < 3600) {
      const minutes = Math.floor(seconds / 60);
      return `${minutes} minute${minutes !== 1 ? 's' : ''} ago`;
    } else {
      const hours = Math.floor(seconds / 3600);
      return `${hours} hour${hours !== 1 ? 's' : ''} ago`;
    }
  };


  // Calculate comparison data for today vs yesterday
  const comparisonData = useMemo(() => {
    if (!timeSeries || !Array.isArray(timeSeries.timeSeries) || timeSeries.timeSeries.length < 2) {
      return {
        todayClaims: 0,
        yesterdayClaims: 0,
        todayLocs: 0,
        yesterdayLocs: 0,
        todayResponses: 0,
        yesterdayResponses: 0,
      };
    }

    const data = timeSeries.timeSeries;
    const today = data[data.length - 1];
    const yesterday = data[data.length - 2];

    return {
      todayClaims: today.claims_received || 0,
      yesterdayClaims: yesterday.claims_received || 0,
      todayLocs: today.locs_sent || 0,
      yesterdayLocs: yesterday.locs_sent || 0,
      todayResponses: today.responses_received || 0,
      yesterdayResponses: yesterday.responses_received || 0,
    };
  }, [timeSeries]);

  useEffect(() => {
    fetchAnalytics();
  }, []);

  if (loading) {
    return (
      <div className="dashboard min-h-screen bg-gradient-to-br from-slate-50 via-white to-cyan-50 px-4 py-10">
        <h2 className="text-2xl font-semibold text-slate-900">Dashboard</h2>
        <div className="loading-message mt-6 rounded-3xl bg-white/60 p-6 shadow-sm">
          Loading analytics...
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="dashboard min-h-screen bg-gradient-to-br from-slate-50 via-white to-cyan-50 px-4 py-10">
        <h2 className="text-2xl font-semibold text-slate-900">Dashboard</h2>
        <div className="error-message mb-4 mt-6 rounded-3xl bg-white/60 p-6 text-rose-600 shadow-sm">
          {error}
        </div>
        <button onClick={fetchAnalytics} className="retry-btn rounded-full bg-slate-900 px-4 py-2 text-sm font-semibold text-white shadow-sm">
          Retry
        </button>
      </div>
    );
  }

  if (!analytics) {
    return (
      <div className="dashboard min-h-screen bg-gradient-to-br from-slate-50 via-white to-cyan-50 px-4 py-10">
        <h2 className="text-2xl font-semibold text-slate-900">Dashboard</h2>
        <div className="error-message mt-6 rounded-3xl bg-white/60 p-6 text-slate-600 shadow-sm">
          No analytics data available
        </div>
      </div>
    );
  }

  const activeClaims = analytics.claimsByStatus['ACTIVE'] || 0;
  const cancelledClaims = analytics.claimsByStatus['CANCELLED'] || 0;
  // Calculate LOC percentage based on non-legacy claims only (legacy claims already have LOCs)
  const claimsNeedingLOCs = analytics.nonLegacyClaims || analytics.totalClaims;
  const locPercentage = claimsNeedingLOCs > 0 ? Math.round((analytics.locsSent / claimsNeedingLOCs) * 100) : 0;
  const cafResults = cafBacklogStats?.lastRun?.results;
  const metricCards: MetricCardConfig[] = [
    {
      key: 'totalClaims',
      label: 'Total Claims',
      value: analytics.totalClaims.toLocaleString(),
      icon: ChartBarIcon,
      accent: 'bg-sky-100 text-sky-600'
    },
    {
      key: 'activeClaims',
      label: 'Active Claims',
      value: activeClaims.toLocaleString(),
      icon: FolderIcon,
      accent: 'bg-emerald-100 text-emerald-600'
    },
    {
      key: 'locsSent',
      label: 'LOCs Sent',
      value: analytics.locsSent.toLocaleString(),
      subtitle: `${locPercentage}% of ${analytics.includesLegacyClaims ? 'non-legacy' : 'all'} claims`,
      icon: EnvelopeIcon,
      accent: 'bg-indigo-100 text-indigo-600'
    },
    {
      key: 'uniqueLenders',
      label: 'Unique Lenders',
      value: analytics.uniqueLenders.toLocaleString(),
      icon: BuildingIcon,
      accent: 'bg-amber-100 text-amber-600'
    },
    {
      key: 'uniqueClients',
      label: 'Unique Clients',
      value: analytics.uniqueClients.toLocaleString(),
      icon: UsersIcon,
      accent: 'bg-rose-100 text-rose-600'
    },
    {
      key: 'cancelledClaims',
      label: 'Cancelled Claims',
      value: cancelledClaims.toLocaleString(),
      icon: SparklesIcon,
      accent: 'bg-orange-100 text-orange-600'
    },
    {
      key: 'recentClaims',
      label: 'Recent Claims',
      value: analytics.recentClaims.toLocaleString(),
      subtitle: 'Last 30 days',
      icon: CalendarIcon,
      accent: 'bg-teal-100 text-teal-600'
    }
  ];
  if (analytics.totalAudits > 0) {
    metricCards.push({
      key: 'totalAudits',
      label: 'Total Audits',
      value: analytics.totalAudits.toLocaleString(),
      icon: SearchIcon,
      accent: 'bg-purple-100 text-purple-600'
    });
  }

  const formatDate = (dateStr: string) => {
    try {
      return new Date(dateStr).toLocaleDateString();
    } catch {
      return dateStr;
    }
  };

  return (
    <div className="dashboard min-h-screen bg-gradient-to-br from-slate-50 via-white to-cyan-50 px-4 py-6 sm:px-6 lg:px-10">
      <div className="mx-auto flex max-w-7xl flex-col gap-8">
        <div className="dashboard-header flex flex-col gap-4 rounded-3xl bg-white/90 p-6 shadow-sm ring-1 ring-slate-100 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.28em] text-slate-500">Operations Overview</p>
            <h2 className="text-2xl font-semibold text-slate-900">
              {showEnhancedView ? 'User Productivity & Performance' : 'Analytics Dashboard'}
            </h2>
          </div>
          <div className="flex flex-col items-start gap-3 sm:flex-row sm:items-center">
            {cacheAge !== null && (
              <span className="cache-age inline-flex items-center gap-2 rounded-full bg-slate-100 px-4 py-1 text-sm font-medium text-slate-600">
                <SparklesIcon className="text-amber-500" />
                Data updated {formatCacheAge(cacheAge)}
              </span>
            )}
            <div className="dashboard-controls flex flex-wrap items-center gap-3">
*** End Patch
              <button
                onClick={() => setShowEnhancedView(!showEnhancedView)}
                className="view-toggle-btn inline-flex items-center gap-2 rounded-full bg-slate-900 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-slate-800"
              >
                {showEnhancedView ? 'Show Analytics Dashboard' : 'Show Productivity View'}
              </button>
              <button
                onClick={refreshCache}
                className={`refresh-btn inline-flex items-center gap-2 rounded-full border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-600 shadow-sm transition hover:border-slate-300 hover:text-slate-900 ${isRefreshing ? 'opacity-70' : ''}`}
                disabled={isRefreshing}
              >
                {isRefreshing ? 'Refreshing...' : 'Refresh Cache'}
              </button>
            </div>
          </div>
          {timeSeries && (
            <div className="date-filter flex flex-col gap-3 rounded-2xl border border-slate-100 bg-white/80 p-4 text-sm text-slate-600 md:flex-row md:items-end">
              <label className="flex flex-col gap-1 text-xs font-semibold uppercase tracking-wide text-slate-500">
                Start Date
                <input
                  type="date"
                  value={dateRange.start}
                  onChange={(e) => setDateRange({ ...dateRange, start: e.target.value })}
                  className="rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 shadow-sm focus:border-cyan-500 focus:outline-none focus:ring-2 focus:ring-cyan-100"
                />
              </label>
              <label className="flex flex-col gap-1 text-xs font-semibold uppercase tracking-wide text-slate-500">
                End Date
                <input
                  type="date"
                  value={dateRange.end}
                  onChange={(e) => setDateRange({ ...dateRange, end: e.target.value })}
                  className="rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 shadow-sm focus:border-cyan-500 focus:outline-none focus:ring-2 focus:ring-cyan-100"
                />
              </label>
              <label className="flex flex-col gap-1 text-xs font-semibold uppercase tracking-wide text-slate-500">
                Interval
                <select
                  value={granularity}
                  onChange={(e) => setGranularity(e.target.value as 'daily' | 'weekly' | 'monthly')}
                  className="rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 shadow-sm focus:border-cyan-500 focus:outline-none focus:ring-2 focus:ring-cyan-100"
                >
                  <option value="daily">Daily</option>
                  <option value="weekly">Weekly</option>
                  <option value="monthly">Monthly</option>
                </select>
              </label>
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={applyDateFilter}
                  className="apply-filter-btn inline-flex items-center rounded-full bg-gradient-to-r from-cyan-500 to-blue-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:from-cyan-600 hover:to-blue-700"
                >
                  Apply Filter
                </button>
                {(dateRange.start || dateRange.end) && (
                  <button
                    onClick={clearDateFilter}
                    className="clear-filter-btn inline-flex items-center rounded-full border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-600 transition hover:border-slate-300 hover:text-slate-900"
                  >
                    Clear
                  </button>
                )}
              </div>
            </div>
          )}
        </div>

      {/* Show Enhanced Productivity View if toggled */}
      {showEnhancedView ? (
        <ProductivityStats />
      ) : (
        <div className="space-y-8">
          {/* Split View Container */}
          <div className="grid gap-6 xl:grid-cols-[minmax(0,1.1fr)_minmax(0,0.9fr)]">
        {/* Left Column - Metrics and Trends */}
        <div className="space-y-6">
          {/* Key Metrics Cards */}
          <div className="metrics-grid grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
            {metricCards.map((card) => {
              const Icon = card.icon;
              return (
                <div
                  key={card.key}
                  className="metric-card flex items-center gap-4 rounded-2xl border border-slate-100 bg-white/90 p-5 shadow-sm ring-1 ring-slate-100"
                >
                  <span className={`metric-icon inline-flex h-12 w-12 items-center justify-center rounded-2xl ${card.accent}`}>
                    <Icon />
                  </span>
                  <div className="metric-content">
                    <h3 className="text-sm font-semibold uppercase tracking-wide text-slate-500">{card.label}</h3>
                    <div className="metric-value text-2xl font-bold text-slate-900">{card.value}</div>
                    {card.subtitle && (
                      <div className="metric-subtitle text-xs font-medium text-slate-500">{card.subtitle}</div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Trends Summary */}
          {summary && (
        <div className="dashboard-section rounded-3xl border border-slate-100 bg-white/90 p-6 shadow-sm ring-1 ring-slate-100">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <h3>Trends Summary</h3>
            {(dateRange.start || dateRange.end) && (
              <span className="text-xs font-medium text-slate-500">
                {dateRange.start ? formatDate(dateRange.start) : 'Beginning'} — {dateRange.end ? formatDate(dateRange.end) : 'Today'}
              </span>
            )}
          </div>
          <div className="trends-grid grid gap-4 md:grid-cols-3">
            <div className="trend-card rounded-2xl border border-slate-100 bg-slate-50/70 p-4">
              <h4>Claims Today</h4>
              <div className="trend-value">{summary.claimTrends.today}</div>
              <div className="trend-change">
                {summary.claimTrends.dailyChange >= 0 ? '+' : ''}{summary.claimTrends.dailyChange} from yesterday
              </div>
              <div className="trend-average">
                Weekly avg: {summary.claimTrends.weeklyAverage}
              </div>
            </div>
            
            <div className="trend-card rounded-2xl border border-slate-100 bg-slate-50/70 p-4">
              <h4>LOCs Sent Today</h4>
              <div className="trend-value">{summary.locTrends.today}</div>
              <div className="trend-average">
                Weekly avg: {summary.locTrends.weeklyAverage}
              </div>
            </div>
            
            {summary.auditTrends && (
              <div className="trend-card rounded-2xl border border-slate-100 bg-slate-50/70 p-4">
                <h4>Audits Today</h4>
                <div className="trend-value">{summary.auditTrends.today}</div>
                <div className="trend-average">
                  Weekly avg: {summary.auditTrends.weeklyAverage}
                </div>
              </div>
            )}
            
            {summary.cancellationTrends && (
              <div className="trend-card rounded-2xl border border-slate-100 bg-slate-50/70 p-4">
                <h4>Cancellations Today</h4>
                <div className="trend-value">{summary.cancellationTrends.today}</div>
                <div className="trend-change">
                  {summary.cancellationTrends.dailyChange >= 0 ? '+' : ''}{summary.cancellationTrends.dailyChange} from yesterday
                </div>
                <div className="trend-average">
                  Weekly avg: {summary.cancellationTrends.weeklyAverage}
                </div>
              </div>
            )}
            
            <div className="trend-card rounded-2xl border border-slate-100 bg-slate-50/70 p-4">
              <h4>Last 7 Days</h4>
              <div className="trend-breakdown">
                <div>Claims: {summary.claimTrends.last7Days}</div>
                <div>LOCs: {summary.locTrends.last7Days}</div>
                {summary.auditTrends && <div>Audits: {summary.auditTrends.last7Days}</div>}
                {summary.cancellationTrends && <div>Cancellations: {summary.cancellationTrends.last7Days}</div>}
              </div>
            </div>
          </div>
            </div>
          )}

          {/* ID Verification Stats */}
          {analytics.idVerification && (
            <div className="dashboard-section rounded-3xl border border-slate-100 bg-white/90 p-6 shadow-sm ring-1 ring-slate-100">
              <h3>ID Verification</h3>
              
              {/* ID Request Status */}
              <div className="id-stats-grid grid gap-4 lg:grid-cols-3">
                <div className="id-stat-card rounded-2xl border border-slate-100 bg-white/80 p-4">
                  <h4>ID Requests</h4>
                  <div className="id-stat-items">
                    <div className="id-stat-item">
                      <span className="id-stat-label">Sent:</span>
                      <span className="id-stat-value">{analytics.idVerification.requests.sent.toLocaleString()}</span>
                    </div>
                    <div className="id-stat-item">
                      <span className="id-stat-label">Not Requested:</span>
                      <span className="id-stat-value">{analytics.idVerification.requests.notRequested.toLocaleString()}</span>
                    </div>
                    <div className="id-stat-item">
                      <span className="id-stat-label">Pending:</span>
                      <span className="id-stat-value">{analytics.idVerification.requests.pending.toLocaleString()}</span>
                    </div>
                    <div className="id-stat-item">
                      <span className="id-stat-label">Verified:</span>
                      <span className="id-stat-value">{analytics.idVerification.requests.verified.toLocaleString()}</span>
                    </div>
                    <div className="id-stat-item">
                      <span className="id-stat-label">Rejected:</span>
                      <span className="id-stat-value">{analytics.idVerification.requests.rejected.toLocaleString()}</span>
                    </div>
                  </div>
                </div>

                <div className="id-stat-card rounded-2xl border border-slate-100 bg-white/80 p-4">
                  <h4>ID Approvals</h4>
                  <div className="id-stat-items">
                    <div className="id-stat-item">
                      <span className="id-stat-label">IDs Received:</span>
                      <span className="id-stat-value">{analytics.idVerification.approvals.totalReceived.toLocaleString()}</span>
                    </div>
                    <div className="id-stat-item">
                      <span className="id-stat-label">Pending Review:</span>
                      <span className="id-stat-value">{analytics.idVerification.approvals.pendingReview.toLocaleString()}</span>
                    </div>
                    <div className="id-stat-item">
                      <span className="id-stat-label">Manually Approved:</span>
                      <span className="id-stat-value">{analytics.idVerification.approvals.manuallyApproved.toLocaleString()}</span>
                    </div>
                    <div className="id-stat-item">
                      <span className="id-stat-label">AI Approved:</span>
                      <span className="id-stat-value">{analytics.idVerification.approvals.aiApproved.toLocaleString()}</span>
                    </div>
                    <div className="id-stat-item">
                      <span className="id-stat-label">Total Approved:</span>
                      <span className="id-stat-value">{analytics.idVerification.approvals.totalApproved.toLocaleString()}</span>
                    </div>
                  </div>
                </div>

                <div className="id-stat-card rounded-2xl border border-slate-100 bg-white/80 p-4">
                  <h4>AI Performance</h4>
                  <div className="id-stat-items">
                    <div className="id-stat-item">
                      <span className="id-stat-label">Processed by AI:</span>
                      <span className="id-stat-value">{analytics.idVerification.aiPerformance.processed.toLocaleString()}</span>
                    </div>
                    <div className="id-stat-item">
                      <span className="id-stat-label">AI Approval Rate:</span>
                      <span className="id-stat-value">{analytics.idVerification.aiPerformance.approvalRate}%</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

        </div>

        {/* Right Column - Charts */}
        <div>
          {timeSeries && Array.isArray(timeSeries.timeSeries) && timeSeries.timeSeries.length > 0 && (
            <div className="dashboard-section rounded-3xl border border-slate-100 bg-white/90 p-6 shadow-sm ring-1 ring-slate-100">
              <h3>Analytics Charts</h3>
              <div className="mt-4 space-y-6">
                <TimeSeriesChart 
                  data={timeSeries.timeSeries} 
                  granularity={granularity}
                  loading={loading}
                />
                <ClaimsComparisonChart 
                  data={comparisonData}
                  loading={loading}
                />
                <StatusDistributionChart 
                  data={analytics.claimsByStatus}
                  loading={loading}
                />
                {analytics.topLenders && analytics.topLenders.length > 0 && (
                  <TopLendersChart 
                    data={analytics.topLenders}
                    loading={loading}
                  />
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Claims Status Breakdown */}
      <div className="dashboard-section rounded-3xl border border-slate-100 bg-white/90 p-6 shadow-sm ring-1 ring-slate-100">
        <h3>Claims by Status</h3>
        <div className="status-breakdown mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {Object.entries(analytics.claimsByStatus).map(([status, count]) => (
            <div key={status} className="status-item flex items-center justify-between rounded-2xl border border-slate-100 bg-slate-50/70 px-4 py-3 text-sm">
              <span className="status-label font-semibold text-slate-600">{status || 'ACTIVE'}</span>
              <span className="status-count font-semibold text-slate-900">
                {count.toLocaleString()}{' '}
                <span className="status-percentage text-xs font-medium text-slate-500">
                  ({analytics.totalClaims > 0 ? Math.round((count / analytics.totalClaims) * 100) : 0}%)
                </span>
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Top Lenders */}
      {analytics.topLenders.length > 0 && (
        <div className="dashboard-section rounded-3xl border border-slate-100 bg-white/90 p-6 shadow-sm ring-1 ring-slate-100">
          <h3>Top Lenders by Claim Count</h3>
          <div className="top-lenders mt-4 space-y-3">
            {analytics.topLenders.slice(0, 10).map((lender, index) => (
              <div key={lender.lender} className="lender-item flex items-center justify-between rounded-2xl border border-slate-100 bg-slate-50/70 px-4 py-3 text-sm">
                <span className="lender-name font-medium text-slate-900">
                  #{index + 1} · {lender.lender}
                </span>
                <span className="lender-count font-semibold text-slate-600">{lender.count} claims</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {cafResults && (
        <div className="dashboard-section rounded-3xl border border-slate-100 bg-white/90 p-6 shadow-sm ring-1 ring-slate-100">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <h3>CAF Backlog Processor</h3>
            <div className="text-xs font-medium text-slate-500">
              Last run:{' '}
              {cafBacklogStats?.lastRun?.time
                ? new Date(cafBacklogStats.lastRun.time).toLocaleString()
                : 'N/A'}
            </div>
          </div>
          <div className="mt-4 grid gap-4 md:grid-cols-2 xl:grid-cols-4">
            <div className="rounded-2xl border border-slate-100 bg-slate-50/70 p-4">
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Processed</p>
              <p className="text-2xl font-bold text-slate-900">{cafResults.totalProcessed.toLocaleString()}</p>
            </div>
            <div className="rounded-2xl border border-slate-100 bg-slate-50/70 p-4">
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Successful</p>
              <p className="text-2xl font-bold text-emerald-600">{cafResults.totalSuccess.toLocaleString()}</p>
            </div>
            <div className="rounded-2xl border border-slate-100 bg-slate-50/70 p-4">
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Errors</p>
              <p className="text-2xl font-bold text-rose-600">{cafResults.totalErrors.toLocaleString()}</p>
            </div>
            <div className="rounded-2xl border border-slate-100 bg-slate-50/70 p-4">
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Queue Pending</p>
              <p className="text-2xl font-bold text-slate-900">
                {cafBacklogStats?.pendingTasks?.toLocaleString() ?? '0'}
              </p>
            </div>
          </div>
          <div className="mt-6 grid gap-4 md:grid-cols-2 xl:grid-cols-4">
            {([
              { label: 'Archived', value: cafResults.tasksArchived, accent: 'text-amber-600' },
              { label: 'Assigned (complete)', value: cafResults.tasksCompleted, accent: 'text-emerald-600' },
              {
                label: 'Assigned (needs follow-up)',
                value: (cafResults.actionCounts?.AUTO_ASSIGN_ONLY ?? 0),
                accent: 'text-sky-600'
              },
              {
                label: 'Manual Review',
                value: cafResults.actionCounts?.MANUAL_REVIEW ?? 0,
                accent: 'text-rose-600'
              }
            ] as const).map((item) => (
              <div key={item.label} className="rounded-2xl border border-slate-100 bg-slate-50/70 p-4">
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">{item.label}</p>
                <p className={`text-2xl font-bold ${item.accent}`}>{item.value.toLocaleString()}</p>
              </div>
            ))}
          </div>
        </div>
      )}



        </div>
      )}
    </div>
  </div>
  );
};

export default Dashboard; 
